import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { can, formatQty, type RestockSendInput } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { accountRefKey, sameAccount, type AccountRef } from '../../lib/account';
import { identity } from '../../lib/identity';
import { refusalStatus } from '../../lib/refusal';
import { restockSendInvalidatesQuery } from '../../lib/restock-query-policy';
import { useSession } from '../session';
import { failedWriteLog, useFailedWriteList } from '../../lib/failed-writes';
import { useQueueRead, useQueuedWrite, useUnsentWrite } from '../ui/useConnection';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { Field, field } from '../create/Field';
import {
  ErrorState,
  OfflineState,
  StaleNotice,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { Skeleton } from '../ui/Skeleton';
import { IconInventory } from '../icons';

import {
  restockSendAttemptForAccount,
  restockSendRecoveryAcceptedForAccount,
  restockSendErrorForAccount,
  restockSendRetryEnabled,
  restockSendStorageErrorMessage,
  classifyRestockSendFailure,
  loadRestockSendAttempt,
  hasUnreadableRestockSendAttempt,
  saveRestockSendAttempt,
  markRestockSendAttemptAccepted,
  restoreAcceptedRestockSendAttempt,
  clearRestockSendAttempt,
  clearUnreadableRestockSendAttempt,
  reconcileRestockSendAttemptAfterClearFailure,
  restockAttemptQuantity,
  type RestockSendAttempt,
} from '../../lib/restock-send-recovery';
export {
  restockSendAttemptForAccount,
  restockSendRecoveryAcceptedForAccount,
  restockSendErrorForAccount,
  restockSendRetryEnabled,
  restockSendStorageErrorMessage,
  classifyRestockSendFailure,
  loadRestockSendAttempt,
  hasUnreadableRestockSendAttempt,
  saveRestockSendAttempt,
  markRestockSendAttemptAccepted,
  restoreAcceptedRestockSendAttempt,
  clearRestockSendAttempt,
  clearUnreadableRestockSendAttempt,
  reconcileRestockSendAttemptAfterClearFailure,
  restockAttemptQuantity,
} from '../../lib/restock-send-recovery';
type RestockSendOwnerState = {
  retryAttempt: RestockSendAttempt | null;
  unreadableSavedAttempt: boolean;
  storageError: boolean;
  storageSaveError: boolean;
  sendConfirmed: boolean;
  slotConflict: boolean;
  slotConflictStoredMutationId: string | null;
};

type RestockRow = {
  restockId: string;
  restockNumber: number;
  vanId: string;
  vanName: string;
  state: 'sent' | 'checked';
  sentAt: string;
  sentBy: string;
  sentByName: string | null;
  checkedAt: string | null;
  checkedBy: string | null;
  checkedByName?: string | null;
  itemCount: number;
};

function qtyText(hundredths: number) {
  return formatQty(hundredths);
}

function asHundredths(value: string): number {
  if (!/^\d+(?:\.\d{0,2})?$/.test(value)) return Number.NaN;
  const [whole, fraction = ''] = value.split('.');
  const amount = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(amount) ? amount : Number.NaN;
}

function asQuantity(value: number) {
  return value % 100 === 0
    ? String(value / 100)
    : `${Math.floor(value / 100)}.${String(value % 100).padStart(2, '0')}`;
}

export function RestockCards({
  vanId,
  itemNames,
}: {
  vanId: string;
  itemNames?: ReadonlyMap<string, string>;
}) {
  const trpc = useTRPC();
  const [selected, setSelected] = useState<string | null>(null);
  const [beforeNumber, setBeforeNumber] = useState<number | null>(null);
  const query = useQuery(
    trpc.restocks.list.queryOptions({ vanId, ...(beforeNumber ? { beforeNumber } : {}) }),
  );
  const state = shownState(query, query.data);
  if (state === 'offline')
    return <OfflineState icon={IconInventory} what="restocks for your van" />;
  if (state === 'loading') return <Skeleton className="h-16" />;
  if (state === 'error')
    return (
      <ErrorState
        icon={IconInventory}
        what="restocks for your van"
        onRetry={() => void query.refetch()}
      />
    );
  const rows = (query.data?.items ?? []) as RestockRow[];
  const unchecked =
    query.data?.uncheckedCount ?? rows.filter((row) => row.state === 'sent').length;
  const tags = (query.data?.itemTags ?? []).filter((item) => itemNames?.has(item.itemId));
  return (
    <section aria-label="Restocks for this van" className="space-y-2">
      <StaleNotice
        show={refreshFailed(query, query.data)}
        onRetry={() => void query.refetch()}
      />
      {unchecked > 0 ? (
        <p className="text-sm font-medium">
          {unchecked} {unchecked === 1 ? 'restock to check' : 'restocks to check'}
        </p>
      ) : null}
      {tags.length > 0 ? (
        <div aria-label="Quantities sent across restocks" className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">
            Across sent restocks
          </p>
          <ul className="flex flex-wrap gap-x-3 gap-y-1">
            {tags.map((item) => (
              <li key={item.itemId} className="text-xs text-muted-foreground">
                {itemNames!.get(item.itemId)} · {qtyText(item.sentHundredths)} sent across
                restocks
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No restocks have been sent to this van.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-[var(--radius)] border border-border bg-surface">
          {rows.map((row) => (
            <li key={row.restockId} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  R-{row.restockNumber} · {row.vanName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {row.itemCount} {row.itemCount === 1 ? 'part' : 'parts'} sent
                </p>
                {row.state === 'checked' ? (
                  <StatusBadge tone="success">Checked</StatusBadge>
                ) : null}
              </div>
              <Button
                className="min-h-11"
                size="sm"
                variant={row.state === 'sent' ? 'primary' : 'ghost'}
                onClick={() => setSelected(row.restockId)}
              >
                {row.state === 'sent' ? 'Check' : 'View'}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        {beforeNumber ? (
          <Button
            className="min-h-11"
            size="sm"
            variant="ghost"
            onClick={() => setBeforeNumber(null)}
          >
            Newer restocks
          </Button>
        ) : null}
        {query.data?.hasMore && query.data.nextBeforeNumber ? (
          <Button
            className="min-h-11"
            size="sm"
            variant="ghost"
            onClick={() => setBeforeNumber(query.data!.nextBeforeNumber)}
          >
            Older restocks
          </Button>
        ) : null}
      </div>
      {selected ? (
        <RestockCheckSheet restockId={selected} onClose={() => setSelected(null)} />
      ) : null}
    </section>
  );
}

export function RestockCheckSheet({
  restockId,
  onClose,
}: {
  restockId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const session = useSession();
  const queueRead = useQueueRead();
  const query = useQuery(trpc.restocks.get.queryOptions({ restockId }));
  const detail = query.data as any;
  const items = detail?.items ?? [];
  const office = can(session.role, 'inventory', 'manage');
  const [checkMode, setCheckMode] = useState<'all' | 'issues'>('all');
  const issueMode = checkMode === 'issues';
  const [values, setValues] = useState<
    Record<string, { missing: string; damaged: string }>
  >({});
  const [returnLocationId, setReturnLocationId] = useState('');
  const [keys] = useState(() => createIntentKey());
  const pending = useMutation(trpc.restocks.check.mutationOptions());
  const unsent = useUnsentWrite('restocks.check', restockId, 'restockId');
  const queued = useQueuedWrite('restocks.check', restockId, 'restockId');
  const failedRows = useFailedWriteList().filter(
    (row) =>
      row.path === 'restocks.check' &&
      row.variables.restockId === restockId &&
      row.userId === session.userId &&
      row.orgId === session.orgId,
  );
  const refused = failedRows[0];
  const failedRetryable = refused?.retryable === true;
  const mutationBusy = pending.isPending && !pending.isPaused;
  const queuedState = queued || pending.isPaused;
  const serverChecked = detail?.state === 'checked' && detail?.result;
  const state = shownState(query, query.data);
  const locked = mutationBusy || unsent || !queueRead || Boolean(refused) || queuedState;
  const corrections = items
    .map((item: any) => {
      const entry = values[item.itemId];
      return {
        itemId: item.itemId,
        missingHundredths: entry ? asHundredths(entry.missing) : 0,
        damagedHundredths: entry ? asHundredths(entry.damaged) : 0,
      };
    })
    .filter((item: any) => item.missingHundredths > 0 || item.damagedHundredths > 0);
  const invalid =
    Object.values(values).some(
      (entry) =>
        !Number.isSafeInteger(asHundredths(entry.missing)) ||
        !Number.isSafeInteger(asHundredths(entry.damaged)),
    ) ||
    corrections.some(
      (correction: any) =>
        !Number.isSafeInteger(correction.missingHundredths) ||
        !Number.isSafeInteger(correction.damagedHundredths),
    );
  const submit = (allHere: boolean) => {
    const payload = allHere ? [] : corrections;
    const key = keys.forIntent([
      session.orgId,
      session.userId,
      restockId,
      payload,
      returnLocationId || null,
    ]);
    pending.mutate({
      restockId,
      items: payload,
      ...(office && returnLocationId ? { officeReturnLocationId: returnLocationId } : {}),
      ...key,
    });
  };
  const retrySaved = () => refused && void failedWriteLog.retry(refused.mutationId);
  const close = () => {
    if (mutationBusy) return;
    onClose();
  };
  let footer: ReactNode = null;
  if (failedRetryable)
    footer = (
      <div className="space-y-2">
        <Notice tone="warn" alert>
          {refused!.reason}
        </Notice>
        <Button
          className="min-h-11 w-full"
          disabled={!refused!.durable || refused!.sending}
          onClick={retrySaved}
        >
          Retry saved check
        </Button>
      </div>
    );
  else if (refused)
    footer = (
      <div className="space-y-2">
        <Notice tone="danger" alert>
          {refused.reason}
        </Notice>
        <Button className="min-h-11 w-full" disabled>
          All are here
        </Button>
        <button
          type="button"
          className="min-h-11 underline"
          onClick={() => void failedWriteLog.discard(refused.mutationId)}
        >
          Dealt with another way
        </button>
      </div>
    );
  else if (serverChecked)
    footer = (
      <Button className="min-h-11 w-full" variant="ghost" onClick={onClose}>
        Close
      </Button>
    );
  else
    footer = (
      <div className="space-y-2">
        {pending.error ? (
          <Notice tone="danger" alert>
            {pending.error.message}
          </Notice>
        ) : null}
        {queuedState ? (
          <Notice tone="info">
            Pending — saved on this device. It will send when you’re back online.
          </Notice>
        ) : null}
        <Button
          className="min-h-11 w-full"
          disabled={locked || invalid || mutationBusy || items.length === 0}
          onClick={() => submit(!issueMode)}
        >
          {mutationBusy ? 'Sending…' : issueMode ? 'Confirm this check' : 'All are here'}
        </Button>
      </div>
    );
  if (state === 'offline')
    return (
      <Sheet open onClose={onClose} title="Check restock">
        <OfflineState icon={IconInventory} what="this restock" />
      </Sheet>
    );
  if (state === 'loading')
    return (
      <Sheet open onClose={onClose} title="Check restock">
        <Skeleton className="h-36" />
      </Sheet>
    );
  if (state === 'error')
    return (
      <Sheet open onClose={onClose} title="Check restock">
        <ErrorState
          icon={IconInventory}
          what="this restock"
          onRetry={() => void query.refetch()}
        />
      </Sheet>
    );
  return (
    <Sheet
      open
      onClose={close}
      title={`R-${detail.restockNumber} · ${detail.vanName}`}
      badge={
        <StatusBadge tone={serverChecked ? 'success' : 'warn'}>
          {serverChecked ? 'Checked' : 'Sent'}
        </StatusBadge>
      }
      footer={footer}
    >
      <div className="space-y-4">
        <StaleNotice
          show={refreshFailed(query, query.data)}
          onRetry={() => void query.refetch()}
        />
        {serverChecked ? (
          <div>
            <p className="font-medium">
              Checked by{' '}
              {detail.checkedByName ?? detail.result.checkedByName ?? 'the office'}
            </p>
            <p className="text-sm text-muted-foreground">
              {detail.checkedAt
                ? new Date(detail.checkedAt).toLocaleString()
                : detail.result.checkedAt
                  ? new Date(detail.result.checkedAt).toLocaleString()
                  : ''}
            </p>
          </div>
        ) : null}
        {!serverChecked && pending.isPending ? (
          <StatusBadge tone="info">Pending</StatusBadge>
        ) : null}
        {items.map((item: any) => {
          const entry = values[item.itemId] ?? { missing: '0', damaged: '0' };
          const errorId = `restock-error-${item.itemId}`;
          const total = asHundredths(entry.missing) + asHundredths(entry.damaged);
          const hasError = Boolean(pending.error) && !serverChecked;
          return (
            <section
              key={item.itemId}
              className="space-y-2 border-b border-border pb-3 last:border-0"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="break-words font-medium">{item.name}</h3>
                  {item.sku ? (
                    <p className="text-xs text-muted-foreground">{item.sku}</p>
                  ) : null}
                </div>
                <span className="shrink-0 tabular-nums">
                  Sent {qtyText(item.sentHundredths)}
                </span>
              </div>
              {serverChecked ? (
                (() => {
                  const result = detail.result.items.find(
                    (line: any) => line.itemId === item.itemId,
                  );
                  return (
                    <p className="text-sm">
                      Confirmed{' '}
                      {qtyText(
                        result?.confirmedHundredths ?? item.confirmedHundredths ?? 0,
                      )}{' '}
                      · Missing{' '}
                      {qtyText(result?.missingHundredths ?? item.missingHundredths ?? 0)}{' '}
                      · Damaged{' '}
                      {qtyText(result?.damagedHundredths ?? item.damagedHundredths ?? 0)}
                    </p>
                  );
                })()
              ) : issueMode ? (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Missing">
                    <input
                      id={`missing-${item.itemId}`}
                      className={`${field} min-h-11`}
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={entry.missing}
                      disabled={locked || mutationBusy}
                      aria-label={`Missing for ${item.name}`}
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? errorId : undefined}
                      onChange={(event) =>
                        setValues((previous) => ({
                          ...previous,
                          [item.itemId]: { ...entry, missing: event.target.value },
                        }))
                      }
                    />
                  </Field>
                  <Field label="Damaged">
                    <input
                      id={`damaged-${item.itemId}`}
                      className={`${field} min-h-11`}
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      value={entry.damaged}
                      disabled={locked || mutationBusy}
                      aria-label={`Damaged for ${item.name}`}
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? errorId : undefined}
                      onChange={(event) =>
                        setValues((previous) => ({
                          ...previous,
                          [item.itemId]: { ...entry, damaged: event.target.value },
                        }))
                      }
                    />
                  </Field>
                  {hasError ? (
                    <span id={errorId} className="sr-only">
                      {pending.error?.message}
                    </span>
                  ) : null}
                  {total > item.sentHundredths ? (
                    <p className="col-span-2 text-sm text-[var(--sw-danger-text)]">
                      Missing and damaged cannot exceed the quantity sent.
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {item.owedHundredths > 0
                    ? `Still owed ${qtyText(item.owedHundredths)}`
                    : 'Confirm what arrived.'}
                </p>
              )}
            </section>
          );
        })}
        {!serverChecked && !issueMode ? (
          <Button
            className="min-h-11 w-full"
            variant="ghost"
            disabled={locked || mutationBusy}
            onClick={() => setCheckMode('issues')}
          >
            Something’s missing
          </Button>
        ) : null}
        {!serverChecked && issueMode && office && detail.returnLocations?.length ? (
          <Field label="Office return destination">
            <select
              id="office-return-location"
              className={`${field} min-h-11`}
              disabled={locked}
              value={returnLocationId}
              onChange={(event) => setReturnLocationId(event.target.value)}
            >
              <option value="">Use the original shelf</option>
              {detail.returnLocations.map((location: any) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {!serverChecked && queuedState ? (
          <p className="text-sm">Pending — saved on this device</p>
        ) : null}
        {serverChecked ? (
          <p className="text-sm">
            Confirmed{' '}
            {qtyText(
              detail.result.items.reduce(
                (sum: number, item: any) => sum + item.confirmedHundredths,
                0,
              ),
            )}{' '}
            · Missing{' '}
            {qtyText(
              detail.result.items.reduce(
                (sum: number, item: any) => sum + item.missingHundredths,
                0,
              ),
            )}{' '}
            · Damaged{' '}
            {qtyText(
              detail.result.items.reduce(
                (sum: number, item: any) => sum + item.damagedHundredths,
                0,
              ),
            )}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

export function RestockSendSheet({
  vanId,
  onClose,
}: {
  vanId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const initialOwner = { orgId: session.orgId, userId: session.userId };
  const [proposal, setProposal] = useState<Record<string, string> | null>(null);
  const [acknowledgeNegative, setAcknowledgeNegative] = useState(false);
  const [reviewedRevision, setReviewedRevision] = useState<string | null>(null);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [reviewStage, setReviewStage] = useState<'none' | 'refresh' | 'confirm'>('none');
  const [retryAttempt, setRetryAttempt] = useState<RestockSendAttempt | null>(() =>
    loadRestockSendAttempt(initialOwner, vanId),
  );
  const retryAttemptOwner = useRef<AccountRef | null>(retryAttempt ? initialOwner : null);
  const [unreadableSavedAttempt, setUnreadableSavedAttempt] = useState(() =>
    hasUnreadableRestockSendAttempt(initialOwner, vanId),
  );
  const [storageError, setStorageError] = useState(false);
  const [storageSaveError, setStorageSaveError] = useState(false);
  const [sendConfirmed, setSendConfirmed] = useState(false);
  const [slotConflict, setSlotConflict] = useState(false);
  const [slotConflictStoredMutationId, setSlotConflictStoredMutationId] = useState<
    string | null
  >(null);
  const setSlotConflictState = (conflict: boolean, mutationId: string | null = null) => {
    setSlotConflict(conflict);
    setSlotConflictStoredMutationId(conflict ? mutationId : null);
  };
  const sendOwner = useRef<AccountRef>(initialOwner);
  const claimingSend = useRef(false);
  const [sendOwnerForRender, setSendOwnerForRender] = useState(initialOwner);
  const sendErrorOwner = useRef<AccountRef | null>(initialOwner);
  const recoveryByOwner = useRef(new Map<string, RestockSendOwnerState>());
  const account = { orgId: session.orgId, userId: session.userId };
  const setOwnedRetryAttempt = (
    owner: AccountRef,
    attempt: RestockSendAttempt | null,
  ) => {
    if (
      !attempt &&
      retryAttemptOwner.current &&
      !sameAccount(retryAttemptOwner.current, owner)
    )
      return;
    retryAttemptOwner.current = attempt ? { ...owner } : null;
    setRetryAttempt(attempt);
  };
  const adoptSendOwner = (owner: AccountRef) => {
    if (sameAccount(sendOwner.current, owner)) return;
    const previousOwner = sendOwner.current;
    const previousKey = accountRefKey(previousOwner)!;
    recoveryByOwner.current.set(previousKey, {
      retryAttempt,
      unreadableSavedAttempt,
      storageError,
      storageSaveError,
      sendConfirmed,
      slotConflict,
      slotConflictStoredMutationId,
    });
    const nextKey = accountRefKey(owner)!;
    const restored = recoveryByOwner.current.get(nextKey);
    const savedAttempt = loadRestockSendAttempt(owner, vanId);
    sendOwner.current = { ...owner };
    setSendOwnerForRender({ ...owner });
    const nextAttempt = restored?.retryAttempt ?? savedAttempt;
    const restoredAccepted = Boolean(restored?.sendConfirmed && restored.retryAttempt);
    const savedAccepted = Boolean(
      savedAttempt?.accepted && savedAttempt.mutationId === nextAttempt?.mutationId,
    );
    retryAttemptOwner.current = nextAttempt ? { ...owner } : null;
    setRetryAttempt(nextAttempt);
    setUnreadableSavedAttempt(
      hasUnreadableRestockSendAttempt(owner, vanId) ||
        Boolean(restored?.unreadableSavedAttempt),
    );
    setStorageError(restored?.storageError ?? false);
    setStorageSaveError(Boolean(restored?.storageSaveError && !savedAttempt));
    setSendConfirmed(restoredAccepted || savedAccepted);
    const restoredConflict = Boolean(
      restored?.slotConflict &&
        savedAttempt?.mutationId === restored.slotConflictStoredMutationId,
    );
    const currentSlotDiffers = Boolean(
      restored?.retryAttempt &&
        savedAttempt &&
        restored.retryAttempt.mutationId !== savedAttempt.mutationId,
    );
    setSlotConflictState(
      restoredConflict || currentSlotDiffers,
      restoredConflict
        ? (restored?.slotConflictStoredMutationId ?? null)
        : currentSlotDiffers
          ? (savedAttempt?.mutationId ?? null)
          : null,
    );
    setProposal(null);
    setAcknowledgeNegative(false);
    setReviewedRevision(null);
    setReviewRequested(false);
    setReviewStage('none');
  };
  const [keys] = useState(() => createIntentKey());
  const visibleRetryAttempt = restockSendAttemptForAccount(
    retryAttempt,
    retryAttemptOwner.current,
    account,
  );
  const recoveryAcceptedForOwner = restockSendRecoveryAcceptedForAccount(
    retryAttempt,
    retryAttemptOwner.current,
    sendConfirmed,
    sendOwnerForRender,
    sendOwnerForRender,
  );
  const visibleRetryAccepted = restockSendRecoveryAcceptedForAccount(
    retryAttempt,
    retryAttemptOwner.current,
    sendConfirmed,
    sendOwnerForRender,
    account,
  );
  const statusBelongsToAccount = sameAccount(sendOwnerForRender, account);
  const proposalForAccount = statusBelongsToAccount ? proposal : null;
  const acknowledgeNegativeForAccount = statusBelongsToAccount
    ? acknowledgeNegative
    : false;
  const unreadableSavedAttemptForAccount =
    unreadableSavedAttempt && statusBelongsToAccount;
  const storageErrorForAccount = storageError && statusBelongsToAccount;
  const storageSaveErrorForAccount = storageSaveError && statusBelongsToAccount;
  const slotConflictForAccount = slotConflict && statusBelongsToAccount;
  const itemsInput = proposalForAccount
    ? Object.entries(proposalForAccount).map(([itemId, value]) => ({
        itemId,
        sendHundredths: asHundredths(value),
      }))
    : undefined;
  const preview = useQuery(
    trpc.restocks.previewSend.queryOptions({
      vanId,
      ...(itemsInput ? { items: itemsInput } : {}),
      ...(acknowledgeNegativeForAccount ? { acknowledgeNegative: true } : {}),
    }),
  );
  const data = preview.data as any;
  useEffect(() => {
    if (
      statusBelongsToAccount &&
      reviewRequested &&
      proposalForAccount &&
      !preview.isFetching &&
      data?.revision
    ) {
      setReviewedRevision(data.revision);
      setReviewRequested(false);
    }
  }, [
    statusBelongsToAccount,
    reviewRequested,
    proposalForAccount,
    preview.isFetching,
    data?.revision,
  ]);
  const send = useMutation(
    trpc.restocks.send.mutationOptions({
      networkMode: 'always',
      retry: false,
      onSettled: () => {
        claimingSend.current = false;
      },
      onSuccess: async (_result, variables) => {
        const owner = { ...sendOwner.current };
        keys.clear();
        const snapshot = identity.cacheAccount.snapshot();
        const current = snapshot.status === 'settled' ? snapshot.account : null;
        if (sameAccount(owner, current))
          void qc.invalidateQueries({
            predicate: (query) => restockSendInvalidatesQuery(query.queryKey[0]),
          });
        await markRestockSendAttemptAccepted(owner, vanId, variables.mutationId);
        if (!(await clearRestockSendAttempt(owner, vanId, variables.mutationId))) {
          const latest = loadRestockSendAttempt(owner, vanId);
          const different = latest && latest.mutationId !== variables.mutationId;
          setOwnedRetryAttempt(
            owner,
            different ? latest : { ...variables, accepted: true },
          );
          setSendConfirmed(different ? Boolean(latest.accepted) : true);
          setSlotConflictState(Boolean(different), different ? latest!.mutationId : null);
          setStorageError(!different);
          setStorageSaveError(false);
          setUnreadableSavedAttempt(hasUnreadableRestockSendAttempt(owner, vanId));
          return;
        }
        setOwnedRetryAttempt(owner, null);
        setUnreadableSavedAttempt(false);
        setSendConfirmed(false);
        setSlotConflictState(false);
        setStorageError(false);
        setStorageSaveError(false);
        const now = identity.cacheAccount.snapshot();
        if (now.status === 'settled' && sameAccount(owner, now.account)) onClose();
      },
      onError: async (error, variables) => {
        const owner = { ...sendOwner.current };
        const recovery = classifyRestockSendFailure(error, variables);
        const retryForOwner = restockSendAttemptForAccount(
          retryAttempt,
          retryAttemptOwner.current,
          owner,
        );
        if (recovery.kind === 'review') {
          const saved = loadRestockSendAttempt(owner, vanId);
          const accepted =
            (saved?.mutationId === variables.mutationId && saved.accepted === true) ||
            (retryForOwner?.mutationId === variables.mutationId &&
              (retryForOwner.accepted === true || sendConfirmed));
          if (accepted) {
            const acceptedPersisted =
              (saved?.mutationId === variables.mutationId && saved.accepted === true) ||
              (await markRestockSendAttemptAccepted(owner, vanId, variables.mutationId));
            setOwnedRetryAttempt(owner, { ...variables, accepted: true });
            setSendConfirmed(true);
            setSlotConflictState(
              Boolean(saved && saved.mutationId !== variables.mutationId),
              saved && saved.mutationId !== variables.mutationId
                ? saved.mutationId
                : null,
            );
            setStorageError(!acceptedPersisted);
            setStorageSaveError(false);
            setUnreadableSavedAttempt(hasUnreadableRestockSendAttempt(owner, vanId));
            setReviewedRevision(null);
            setReviewStage('none');
            return;
          }
          if (await clearRestockSendAttempt(owner, vanId, variables.mutationId)) {
            setOwnedRetryAttempt(owner, null);
            setSendConfirmed(false);
            setSlotConflictState(false);
            setStorageError(false);
            setStorageSaveError(false);
            setUnreadableSavedAttempt(false);
            setReviewedRevision(null);
            setReviewStage('refresh');
          } else {
            const latest = loadRestockSendAttempt(owner, vanId);
            const different = latest && latest.mutationId !== variables.mutationId;
            setOwnedRetryAttempt(owner, different ? latest : variables);
            setSendConfirmed(Boolean(different && latest.accepted));
            setSlotConflictState(
              Boolean(different),
              different ? latest!.mutationId : null,
            );
            setStorageError(!different);
            setStorageSaveError(false);
            setUnreadableSavedAttempt(hasUnreadableRestockSendAttempt(owner, vanId));
          }
        } else {
          const saved = loadRestockSendAttempt(owner, vanId);
          const accepted =
            (saved?.mutationId === variables.mutationId && saved.accepted === true) ||
            (retryForOwner?.mutationId === variables.mutationId &&
              (retryForOwner.accepted === true || sendConfirmed));
          setOwnedRetryAttempt(owner, {
            ...recovery.variables,
            ...(accepted ? { accepted: true } : {}),
          });
          setSendConfirmed(accepted);
          setSlotConflictState(
            Boolean(saved && saved.mutationId !== variables.mutationId),
            saved && saved.mutationId !== variables.mutationId ? saved.mutationId : null,
          );
          setUnreadableSavedAttempt(hasUnreadableRestockSendAttempt(owner, vanId));
          setReviewStage('none');
        }
      },
    }),
  );
  const sendErrorForAccount = restockSendErrorForAccount(
    send.error,
    sendErrorOwner.current,
    account,
  );
  useEffect(() => {
    if (!send.isPending && !sameAccount(sendOwner.current, account))
      adoptSendOwner(account);
  }, [account.orgId, account.userId, send.isPending]);
  const inputItems = (data?.items ?? []).map((item: any) => ({
    itemId: item.itemId,
    sendHundredths: proposalForAccount
      ? asHundredths(proposalForAccount[item.itemId] ?? asQuantity(item.sendHundredths))
      : item.sendHundredths,
  }));
  const edited = proposalForAccount !== null;
  const needsReview =
    !statusBelongsToAccount ||
    reviewStage !== 'none' ||
    Boolean(sendErrorForAccount) ||
    (edited && reviewedRevision !== data?.revision);
  const canSend =
    Boolean(data?.revision) &&
    statusBelongsToAccount &&
    !visibleRetryAttempt &&
    !needsReview &&
    !send.isPending &&
    inputItems.every((item: any) => Number.isSafeInteger(item.sendHundredths));
  const submit = async () => {
    if (!data || !canSend || claimingSend.current) return;
    claimingSend.current = true;
    const key = keys.forIntent([
      account.orgId,
      account.userId,
      vanId,
      data.revision,
      inputItems,
      acknowledgeNegativeForAccount,
    ]);
    const variables: RestockSendInput = {
      vanId,
      expectedRevision: data.revision,
      items: inputItems,
      acknowledgeNegative: acknowledgeNegativeForAccount,
      ...key,
    };
    const owner = account;
    adoptSendOwner(owner);
    if (!(await saveRestockSendAttempt(owner, vanId, variables))) {
      claimingSend.current = false;
      const latest = loadRestockSendAttempt(owner, vanId);
      if (latest) {
        setOwnedRetryAttempt(owner, latest);
        setSendConfirmed(latest.accepted === true);
        setSlotConflictState(
          latest.mutationId !== variables.mutationId,
          latest.mutationId !== variables.mutationId ? latest.mutationId : null,
        );
        setStorageError(false);
        setStorageSaveError(false);
        setUnreadableSavedAttempt(false);
        return;
      }
      setStorageError(false);
      setStorageSaveError(true);
      setUnreadableSavedAttempt(hasUnreadableRestockSendAttempt(owner, vanId));
      return;
    }
    setStorageError(false);
    setStorageSaveError(false);
    setUnreadableSavedAttempt(false);
    sendErrorOwner.current = { ...owner };
    const active = identity.cacheAccount.snapshot();
    if (active.status !== 'settled' || !sameAccount(owner, active.account)) {
      claimingSend.current = false;
      return;
    }
    send.mutate(variables);
  };
  const retrySameSend = async () => {
    const owner = retryAttemptOwner.current;
    if (!visibleRetryAttempt || !owner || send.isPending || claimingSend.current) return;
    claimingSend.current = true;
    adoptSendOwner(owner);
    send.reset();
    sendErrorOwner.current = { ...owner };
    const { accepted: _accepted, ...request } = visibleRetryAttempt;
    if (!(await saveRestockSendAttempt(owner, vanId, request))) {
      claimingSend.current = false;
      setStorageSaveError(true);
      return;
    }
    const active = identity.cacheAccount.snapshot();
    if (active.status !== 'settled' || !sameAccount(owner, active.account)) {
      claimingSend.current = false;
      return;
    }
    send.mutate(request);
  };
  const clearSavedAttempt = async (confirmUnreadable = false) => {
    if ((!visibleRetryAttempt && !unreadableSavedAttemptForAccount) || send.isPending)
      return;
    const owner = visibleRetryAttempt ? retryAttemptOwner.current : sendOwner.current;
    if (!owner || !sameAccount(owner, account)) return;
    adoptSendOwner(owner);
    const attempt = restockSendAttemptForAccount(
      retryAttempt,
      retryAttemptOwner.current,
      owner,
    );
    const cleared = attempt
      ? await clearRestockSendAttempt(owner, vanId, attempt.mutationId)
      : await clearUnreadableRestockSendAttempt(owner, vanId);
    const currentIdentity = identity.cacheAccount.snapshot();
    if (
      currentIdentity.status !== 'settled' ||
      !sameAccount(currentIdentity.account, owner) ||
      !sameAccount(sendOwner.current, owner)
    )
      return;
    if (!cleared) {
      const latest = loadRestockSendAttempt(owner, vanId);
      if (latest) {
        const recovery = reconcileRestockSendAttemptAfterClearFailure(
          latest,
          attempt,
          sendConfirmed,
        );
        setOwnedRetryAttempt(owner, recovery.attempt);
        setSendConfirmed(recovery.accepted);
        setSlotConflictState(
          recovery.different,
          recovery.different ? latest.mutationId : null,
        );
        setUnreadableSavedAttempt(false);
        setStorageError(!recovery.different);
        setStorageSaveError(false);
        return;
      }
      const unreadable = hasUnreadableRestockSendAttempt(owner, vanId);
      if (
        unreadable &&
        confirmUnreadable &&
        (await clearUnreadableRestockSendAttempt(owner, vanId))
      ) {
        const stillCurrent = identity.cacheAccount.snapshot();
        if (
          stillCurrent.status !== 'settled' ||
          !sameAccount(stillCurrent.account, owner) ||
          !sameAccount(sendOwner.current, owner)
        )
          return;
        const accepted = Boolean(attempt && (attempt.accepted === true || sendConfirmed));
        if (attempt && accepted) {
          const acceptedAttempt = { ...attempt, accepted: true };
          const persisted = await restoreAcceptedRestockSendAttempt(
            owner,
            vanId,
            acceptedAttempt,
          );
          const restoredIdentity = identity.cacheAccount.snapshot();
          if (
            restoredIdentity.status !== 'settled' ||
            !sameAccount(restoredIdentity.account, owner) ||
            !sameAccount(sendOwner.current, owner)
          )
            return;
          setOwnedRetryAttempt(owner, acceptedAttempt);
          setSendConfirmed(true);
          setStorageError(!persisted);
          setStorageSaveError(false);
        } else {
          setOwnedRetryAttempt(owner, null);
          setSendConfirmed(false);
          setStorageError(false);
          setStorageSaveError(false);
        }
        setUnreadableSavedAttempt(false);
        setSlotConflictState(false);
        setReviewedRevision(null);
        setReviewStage('refresh');
        send.reset();
        sendErrorOwner.current = null;
        void preview.refetch();
        return;
      }
      setUnreadableSavedAttempt(unreadable);
      setStorageError(true);
      setStorageSaveError(false);
      return;
    }
    setOwnedRetryAttempt(owner, null);
    setUnreadableSavedAttempt(false);
    setSendConfirmed(false);
    setSlotConflictState(false);
    setStorageError(false);
    setStorageSaveError(false);
    setReviewedRevision(null);
    setReviewStage('refresh');
    send.reset();
    sendErrorOwner.current = null;
    void preview.refetch();
  };
  const footer = (
    <div className="space-y-2">
      {slotConflictForAccount ? (
        <Notice tone="warn" alert>
          Another unresolved Send is saved for this van. This panel keeps the request
          whose outcome is being checked; resolve it before starting another Send.
        </Notice>
      ) : null}
      {storageErrorForAccount ? (
        <Notice tone="danger" alert>
          {restockSendStorageErrorMessage(recoveryAcceptedForOwner)}
        </Notice>
      ) : null}
      {storageSaveErrorForAccount ? (
        <Notice tone="danger" alert>
          This Send was not submitted because this device could not save its request.
          Resolve any saved batch for this van in Reorder, then check browser storage and
          coordination before trying again.
        </Notice>
      ) : null}
      {unreadableSavedAttemptForAccount ? (
        <Notice tone="danger" alert>
          {recoveryAcceptedForOwner
            ? 'This device has an unreadable saved Send for this van. Keep the accepted receipt, and ask an inventory manager to verify that no other Send is pending before clearing it.'
            : 'This device has an unreadable saved Send for this van. Ask an inventory manager to verify that no restock was created before clearing it.'}
        </Notice>
      ) : null}
      {sendErrorForAccount ? (
        <Notice tone="danger" alert>
          {sendErrorForAccount.message}
        </Notice>
      ) : null}
      {preview.error ? (
        <Notice tone="danger" alert>
          {preview.error.message}
        </Notice>
      ) : null}
      {(
        visibleRetryAttempt
          ? visibleRetryAttempt.acknowledgeNegative
          : data?.items?.some((item: any) => item.requiresAcknowledgement)
      ) ? (
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={send.isPending || Boolean(visibleRetryAttempt)}
            checked={
              visibleRetryAttempt?.acknowledgeNegative ?? acknowledgeNegativeForAccount
            }
            onChange={(event) => {
              setAcknowledgeNegative(event.target.checked);
              setReviewedRevision(null);
            }}
          />
          I’ve reviewed the negative stock warning
        </label>
      ) : null}
      {visibleRetryAttempt ? (
        <>
          <Notice tone="warn" alert>
            {visibleRetryAccepted
              ? 'This Send was accepted. Retry the saved request with the same key, or clear its accepted receipt.'
              : refusalStatus(sendErrorForAccount) === 403
                ? 'Your access is currently denied. The saved request may already have been accepted. Ask an inventory manager to check this van, then restore access and retry the same key or clear the saved request.'
                : 'The Send outcome is unknown. Retry the saved request with the same key to safely confirm whether it was accepted.'}
          </Notice>
          <Button
            className="min-h-11 w-full"
            disabled={
              !restockSendRetryEnabled(retryAttemptOwner.current, account, send.isPending)
            }
            onClick={retrySameSend}
          >
            {send.isPending
              ? 'Checking Send…'
              : refusalStatus(sendErrorForAccount) === 403
                ? 'Retry the same Send after access is restored'
                : 'Retry the same Send'}
          </Button>
          {visibleRetryAccepted ? (
            <Button
              className="min-h-11 w-full"
              variant="ghost"
              disabled={send.isPending}
              onClick={() => clearSavedAttempt()}
            >
              Clear the accepted Send receipt
            </Button>
          ) : (
            <Button
              className="min-h-11 w-full"
              variant="ghost"
              disabled={send.isPending}
              onClick={() => clearSavedAttempt(true)}
            >
              An inventory manager confirmed no restock was created — discard the saved
              Send
            </Button>
          )}
          {unreadableSavedAttemptForAccount && visibleRetryAccepted ? (
            <Button
              className="min-h-11 w-full"
              variant="ghost"
              disabled={send.isPending}
              onClick={() => clearSavedAttempt(true)}
            >
              An inventory manager verified no other Send is pending — clear the
              unreadable saved slot
            </Button>
          ) : null}
        </>
      ) : unreadableSavedAttemptForAccount ? (
        <Button
          className="min-h-11 w-full"
          variant="ghost"
          disabled={send.isPending}
          onClick={() => clearSavedAttempt(true)}
        >
          {recoveryAcceptedForOwner
            ? 'An inventory manager verified no other Send is pending — clear the unreadable saved slot'
            : 'An inventory manager confirmed no restock was created — clear the unreadable saved Send'}
        </Button>
      ) : needsReview && !send.isPending ? (
        <Button
          className="min-h-11 w-full"
          disabled={!statusBelongsToAccount || preview.isFetching}
          onClick={() => {
            if (sendErrorForAccount && reviewStage === 'none') {
              setReviewedRevision(null);
              setReviewStage('refresh');
              return;
            }
            if (reviewStage === 'refresh') {
              void preview.refetch().then((result) => {
                if (result.data?.revision) setReviewStage('confirm');
              });
              return;
            }
            if (reviewStage === 'confirm') {
              setReviewedRevision(data?.revision ?? null);
              setReviewStage('none');
              send.reset();
              sendErrorOwner.current = null;
              return;
            }
            setReviewedRevision(null);
            setReviewRequested(true);
            if (!proposalForAccount && data?.items)
              setProposal(
                Object.fromEntries(
                  data.items.map((item: any) => [
                    item.itemId,
                    asQuantity(item.sendHundredths),
                  ]),
                ),
              );
            else void preview.refetch();
          }}
        >
          {reviewStage === 'confirm'
            ? 'Review refreshed quantities'
            : reviewStage === 'refresh' || sendErrorForAccount
              ? 'Refresh preview'
              : 'Review changes'}
        </Button>
      ) : (
        <Button
          className="min-h-11 w-full"
          disabled={
            !canSend ||
            send.isPending ||
            inputItems.every((item: any) => item.sendHundredths === 0)
          }
          onClick={submit}
        >
          {send.isPending ? 'Sending…' : 'Send restock'}
        </Button>
      )}
    </div>
  );
  const state = shownState(preview, preview.data);
  return (
    <Sheet
      open
      onClose={() => {
        if ((!send.isPending || send.isPaused) && !claimingSend.current) onClose();
      }}
      title={
        data
          ? `Send to ${data.vanName}`
          : visibleRetryAttempt
            ? 'Saved restock recovery'
            : 'Send a restock'
      }
      footer={footer}
      wide
    >
      {visibleRetryAttempt ? (
        <Notice tone="info">
          Saved Send for van {data?.vanName ?? visibleRetryAttempt.vanId}:
          <ul className="mt-1 list-disc pl-5">
            {visibleRetryAttempt.items.map((item) => (
              <li key={item.itemId}>
                {(data?.items ?? []).find((row: any) => row.itemId === item.itemId)
                  ?.name ?? item.itemId}{' '}
                · {qtyText(item.sendHundredths)}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {send.isPaused ? (
        <Notice tone="info">
          Pending — saved on this device. It will send when you’re back online.
        </Notice>
      ) : null}
      {state === 'loading' ? (
        <Skeleton className="h-40" />
      ) : state === 'offline' ? (
        <OfflineState icon={IconInventory} what="the reviewed stock plan" />
      ) : state === 'error' ? (
        <ErrorState
          icon={IconInventory}
          what="the stock plan"
          onRetry={() => void preview.refetch()}
        />
      ) : (
        <div className="space-y-4">
          <StaleNotice
            show={refreshFailed(preview, preview.data)}
            onRetry={() => void preview.refetch()}
          />
          {(data?.items ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No watched parts need a restock right now.
            </p>
          ) : null}
          {(data?.items ?? []).map((item: any) => (
            <section
              key={item.itemId}
              className="space-y-2 border-b border-border pb-3 last:border-0"
            >
              <div className="flex justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="break-words font-medium">{item.name}</h3>
                  {item.sku ? (
                    <p className="text-xs text-muted-foreground">{item.sku}</p>
                  ) : null}
                </div>
                <span className="text-sm">
                  Ask {qtyText(item.askHundredths)} · Short{' '}
                  {qtyText(item.shortHundredths)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <label className="min-w-0 flex-1 text-sm" htmlFor={`send-${item.itemId}`}>
                  Send
                </label>
                <input
                  id={`send-${item.itemId}`}
                  className={`${field} min-h-11 max-w-28 text-right tabular-nums`}
                  type="number"
                  disabled={send.isPending || Boolean(visibleRetryAttempt)}
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  aria-label={`Send for ${item.name}`}
                  value={
                    visibleRetryAttempt
                      ? (restockAttemptQuantity(visibleRetryAttempt, item.itemId) ?? '0')
                      : (proposalForAccount?.[item.itemId] ??
                        asQuantity(item.sendHundredths))
                  }
                  onChange={(event) => {
                    setProposal((previous) => ({
                      ...(previous ??
                        Object.fromEntries(
                          (data.items as any[]).map((entry) => [
                            entry.itemId,
                            asQuantity(entry.sendHundredths),
                          ]),
                        )),
                      [item.itemId]: event.target.value,
                    }));
                    setReviewedRevision(null);
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Available {qtyText(item.availableHundredths)} · source{' '}
                {item.legs
                  .map(
                    (leg: any) =>
                      `${leg.sourcePath ?? (leg.locationId === data.feederId ? 'shop' : 'bin')} ${qtyText(leg.quantityHundredths)}`,
                  )
                  .join(', ') || 'none'}
              </p>
              {item.requiresAcknowledgement ? (
                <Notice tone="warn">
                  {item.rootDeficitHundredths > 0
                    ? `This plan needs ${qtyText(item.rootDeficitHundredths)} from the shop root below zero.`
                    : 'This quantity is above the shop’s net stock.'}{' '}
                  Review the stock count before you send.
                </Notice>
              ) : null}
              {item.incoming.map((incoming: any) => (
                <p
                  key={incoming.purchaseOrderId}
                  className="text-xs text-muted-foreground"
                >
                  {incoming.status === 'draft' ? 'Draft' : 'On order'} PO-
                  {incoming.number} · {qtyText(incoming.outstandingHundredths)} incoming
                </p>
              ))}
            </section>
          ))}
        </div>
      )}
    </Sheet>
  );
}

export function RestockTodayPill() {
  const trpc = useTRPC();
  const own = useQuery(trpc.restocks.ownVan.queryOptions());
  const query = useQuery(
    trpc.restocks.list.queryOptions(
      { vanId: own.data?.van?.id ?? undefined },
      { enabled: Boolean(own.data?.van?.id) },
    ),
  );
  const count = query.data?.uncheckedCount ?? 0;
  if (count < 1) return null;
  return (
    <Link
      to="/tech/van"
      className="mx-4 mb-2 inline-flex min-h-11 items-center rounded-[var(--radius)] border border-border px-3 text-sm font-medium"
    >
      {count} {count === 1 ? 'restock' : 'restocks'} to check
    </Link>
  );
}
